import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Input } from '@/components/ui/input';
import { ArrowLeft, Mail, MessageSquare, Search, Users, Inbox } from 'lucide-react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminDashboard() {
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [activeTab, setActiveTab] = useState('contacts');
  const [contacts, setContacts] = useState([]);
  const [subscribers, setSubscribers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);

  const handleLogin = (e) => {
    e.preventDefault();
    if (password === 'madison2025') {
      setAuthenticated(true);
    }
  };

  useEffect(() => {
    if (authenticated) {
      fetchData();
    }
  }, [authenticated]);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [contactsRes, subscribersRes] = await Promise.all([
        axios.get(`${API}/contacts`),
        axios.get(`${API}/subscribers`),
      ]);
      setContacts(contactsRes.data);
      setSubscribers(subscribersRes.data);
    } catch (err) {
      console.error('Failed to fetch data', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredContacts = contacts.filter(c =>
    c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
    c.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
    (c.service || '').toLowerCase().includes(searchQuery.toLowerCase())
  );

  const filteredSubscribers = subscribers.filter(s =>
    s.email.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (!authenticated) {
    return (
      <div className="min-h-screen bg-zinc-950 flex items-center justify-center px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="w-full max-w-sm"
        >
          <h1 className="font-outfit text-3xl font-bold text-zinc-50 mb-2">Admin Access</h1>
          <p className="text-zinc-400 text-sm mb-8 font-manrope">Enter the admin password to continue.</p>
          <form onSubmit={handleLogin} className="space-y-4">
            <Input
              data-testid="admin-password-input"
              type="password"
              placeholder="Password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="bg-transparent border-white/20 text-zinc-50 placeholder:text-zinc-600 rounded-none h-12 focus:border-white focus:ring-white"
            />
            <button
              data-testid="admin-login-btn"
              type="submit"
              className="w-full bg-white text-black py-3 text-sm font-semibold uppercase tracking-wider hover:bg-zinc-200 transition-colors"
            >
              Access Dashboard
            </button>
          </form>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Header */}
      <div className="border-b border-white/5 bg-zinc-950/70 backdrop-blur-xl sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 flex items-center justify-between h-16">
          <div className="flex items-center gap-4">
            <a href="/" data-testid="admin-back-btn" className="text-zinc-400 hover:text-zinc-50 transition-colors">
              <ArrowLeft size={20} />
            </a>
            <h1 className="font-outfit font-bold text-lg text-zinc-50">Dashboard</h1>
          </div>
          <button
            data-testid="admin-refresh-btn"
            onClick={fetchData}
            className="text-xs font-semibold uppercase tracking-wider text-zinc-500 hover:text-zinc-50 transition-colors border border-white/10 px-4 py-2"
          >
            Refresh
          </button>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        {/* Stats */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-12">
          {[
            { icon: Inbox, label: 'Total Contacts', value: contacts.length },
            { icon: Mail, label: 'Subscribers', value: subscribers.length },
            { icon: MessageSquare, label: 'This Month', value: contacts.filter(c => new Date(c.timestamp) > new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)).length },
            { icon: Users, label: 'Unique Emails', value: new Set([...contacts.map(c => c.email), ...subscribers.map(s => s.email)]).size },
          ].map((stat, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: i * 0.1 }}
              data-testid={`admin-stat-${i}`}
              className="bg-zinc-900 border border-white/5 p-6"
            >
              <stat.icon size={20} className="text-zinc-600 mb-3" />
              <p className="font-outfit text-2xl font-bold text-zinc-50">{stat.value}</p>
              <p className="text-xs text-zinc-500 uppercase tracking-wider mt-1">{stat.label}</p>
            </motion.div>
          ))}
        </div>

        {/* Tabs + Search */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 mb-8">
          <div className="flex gap-0">
            {['contacts', 'subscribers'].map((tab) => (
              <button
                key={tab}
                data-testid={`admin-tab-${tab}`}
                onClick={() => { setActiveTab(tab); setSearchQuery(''); }}
                className={`px-6 py-3 text-sm font-semibold uppercase tracking-wider transition-colors ${
                  activeTab === tab
                    ? 'bg-white text-black'
                    : 'bg-zinc-900 text-zinc-400 hover:text-zinc-50 border border-white/5'
                }`}
              >
                {tab}
              </button>
            ))}
          </div>
          <div className="relative w-full sm:w-64">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
            <Input
              data-testid="admin-search"
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="bg-transparent border-white/20 text-zinc-50 placeholder:text-zinc-600 rounded-none h-10 pl-10 focus:border-white focus:ring-white"
            />
          </div>
        </div>

        {/* Tables */}
        <AnimatePresence mode="wait">
          {loading ? (
            <div className="text-center py-20 text-zinc-500">Loading...</div>
          ) : activeTab === 'contacts' ? (
            <motion.div
              key="contacts"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              data-testid="admin-contacts-table"
            >
              {filteredContacts.length === 0 ? (
                <div className="text-center py-20 text-zinc-500 text-sm">No contact submissions yet.</div>
              ) : (
                <div className="border border-white/5 overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/5 hover:bg-transparent">
                        <TableHead className="text-zinc-500 font-semibold uppercase text-xs tracking-wider">Name</TableHead>
                        <TableHead className="text-zinc-500 font-semibold uppercase text-xs tracking-wider">Email</TableHead>
                        <TableHead className="text-zinc-500 font-semibold uppercase text-xs tracking-wider hidden md:table-cell">Company</TableHead>
                        <TableHead className="text-zinc-500 font-semibold uppercase text-xs tracking-wider hidden lg:table-cell">Service</TableHead>
                        <TableHead className="text-zinc-500 font-semibold uppercase text-xs tracking-wider hidden xl:table-cell">Message</TableHead>
                        <TableHead className="text-zinc-500 font-semibold uppercase text-xs tracking-wider">Date</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredContacts.map((c) => (
                        <TableRow key={c.id} className="border-white/5 hover:bg-zinc-900/50">
                          <TableCell className="text-zinc-50 font-medium">{c.name}</TableCell>
                          <TableCell className="text-zinc-400">{c.email}</TableCell>
                          <TableCell className="text-zinc-400 hidden md:table-cell">{c.company || '—'}</TableCell>
                          <TableCell className="text-zinc-400 hidden lg:table-cell">{c.service || '—'}</TableCell>
                          <TableCell className="text-zinc-400 hidden xl:table-cell max-w-xs truncate">{c.message}</TableCell>
                          <TableCell className="text-zinc-500 text-sm">{new Date(c.timestamp).toLocaleDateString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div
              key="subscribers"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              data-testid="admin-subscribers-table"
            >
              {filteredSubscribers.length === 0 ? (
                <div className="text-center py-20 text-zinc-500 text-sm">No subscribers yet.</div>
              ) : (
                <div className="border border-white/5 overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/5 hover:bg-transparent">
                        <TableHead className="text-zinc-500 font-semibold uppercase text-xs tracking-wider">Email</TableHead>
                        <TableHead className="text-zinc-500 font-semibold uppercase text-xs tracking-wider">Subscribed On</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filteredSubscribers.map((s) => (
                        <TableRow key={s.id} className="border-white/5 hover:bg-zinc-900/50">
                          <TableCell className="text-zinc-50 font-medium">{s.email}</TableCell>
                          <TableCell className="text-zinc-500 text-sm">{new Date(s.timestamp).toLocaleDateString()}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </div>
  );
}
